import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  createElement,
  useEffect,
  useRef,
  useState
} from 'react';
import useSWR from 'swr';

import type { ScopeContextValue, ScopeMode, ScopeResponse, ScopeTenant } from './types';

const ScopeContext = createContext<ScopeContextValue | undefined>(undefined);

function toErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}

const jsonFetcher = async <T,>(url: string): Promise<T> => {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return (await response.json()) as T;
};

async function postJson(url: string, body: unknown): Promise<void> {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with status ${response.status}`);
  }
}

type ScopeProviderProps = {
  children: ReactNode;
  autoSelectFirstTenant?: boolean;
};

export function ScopeProvider({ children, autoSelectFirstTenant = true }: ScopeProviderProps) {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const {
    data,
    error,
    isLoading,
    mutate
  } = useSWR<ScopeResponse>('/api/v1/scope', jsonFetcher, {
    revalidateOnFocus: false,
    onError: err => {
      setErrorMessage(toErrorMessage(err));
    },
    onSuccess: () => {
      setErrorMessage(undefined);
    }
  });

  const autoSelectAttemptedRef = useRef(false);

  const selectTenant = useCallback(
    async (tenantId: string) => {
      if (!tenantId) return;
      await postJson('/api/v1/scope/select', { tenantId });
      await mutate();
    },
    [mutate]
  );

  const selectGlobal = useCallback(async () => {
    await postJson('/api/v1/scope/select', { mode: 'global' });
    await mutate();
  }, [mutate]);

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  useEffect(() => {
    if (!autoSelectFirstTenant) {
      return;
    }

    if (autoSelectAttemptedRef.current) {
      return;
    }

    if (isLoading) {
      return;
    }

    const currentTenant = data?.activeTenant;
    const availableTenants = (data?.organizations ?? [])
      .flatMap(org => org.tenants ?? [])
      .filter((tenant): tenant is NonNullable<ScopeResponse['activeTenant']> => Boolean(tenant?.id));

    if (!currentTenant && availableTenants.length >= 1) {
      autoSelectAttemptedRef.current = true;
      void selectTenant(availableTenants[0].id);
    }
  }, [data?.activeTenant, data?.organizations, isLoading, selectTenant]);

  const contextValue = useMemo<ScopeContextValue>(() => {
    const mode: ScopeMode = data?.mode ?? (data?.activeTenant ? 'tenant' : 'global');
    const allowGlobal = Boolean(data?.allowGlobal);
    return {
      mode,
      allowGlobal,
      activeTenant: data?.activeTenant ?? undefined,
      activeOrganization: data?.activeOrganization ?? undefined,
      organizations: data?.organizations ?? [],
      loading: isLoading,
      error: errorMessage ?? toErrorMessage(error),
      selectTenant,
      selectGlobal,
      refresh
    };
  }, [data, isLoading, error, errorMessage, selectTenant, selectGlobal, refresh]);

  return createElement(ScopeContext.Provider, { value: contextValue }, children);
}

export function useScope(): ScopeContextValue {
  const context = useContext(ScopeContext);
  if (!context) {
    throw new Error('useScope must be used within a ScopeProvider');
  }
  return context;
}
